import React, { useState, useEffect, useRef } from 'react';
import { generateClient } from '@aws-amplify/api';
import { Send, RefreshCw, Paperclip, X, Image as ImageIcon } from 'lucide-react';
import { sendVideoSMS,sendConversationMessage } from '../../services/notifications.service';
import * as mutations from '../../graphql/mutations';
import * as queries from '../../graphql/queries';
//import * as subscriptions from '../../graphql/subscriptions';
import { onCreateMessage } from '../../graphql/subscriptions';
import Participants from './SMSConversationParticipant';
import { getParticipants } from '../../services/participantsService';

const client = generateClient();

const messagesByConversationId = /* GraphQL */ `
  query MessagesByConversationId(
    $conversationId: String!
    $timestamp: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $limit: Int
    $nextToken: String
  ) {
    messagesByConversationId(
      conversationId: $conversationId
      timestamp: $timestamp
      sortDirection: $sortDirection
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        content
        name
        conversationId
        direction
        phoneNumber
        timestamp
        read
        readAt
        mediaUrls
      }
      nextToken
    }
  }
`;

const onCreateMessageByConversationId = /* GraphQL */ `
  subscription OnCreateMessageByConversationId($conversationId: String!) {
    onCreateMessage(filter: {conversationId: {eq: $conversationId}}) {
      id
      content
      name
      conversationId
      direction
      phoneNumber
      timestamp
      read
      readAt
      mediaUrls
    }
  }
`;

const SMSConversation = ({ submissionId, conversationId }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [submission, setSubmission] = useState(null);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const [participants, setParticipants] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const fileInputRef = useRef(null);

  // Fetch submission data
  useEffect(() => {
    const fetchSubmission = async () => {
      if (!submissionId) return;
      
      try {
        const submissionData = await client.graphql({
          query: queries.getSubmission,
          variables: { id: submissionId }
        });
        
        setSubmission(submissionData.data.getSubmission);
      } catch (error) {
        console.error('Error fetching submission:', error);
        setError('Failed to load submission data');
      }
    };
    
    fetchSubmission();
  }, [submissionId]);
  
  // Fetch participants
  useEffect(() => {
    const fetchParticipants = async () => {
      if (!conversationId) return;
      try {
        const data = await getParticipants(conversationId); // ✅ calling the service
        setParticipants(data);
      } catch (error) {
        console.error('[SMSConversation] Error loading participants:', error);
      }
    };

    fetchParticipants();
  }, [conversationId]);

  // Fetch messages
  useEffect(() => {
    let isMounted = true;
  
    const fetchMessages = async () => {
      if (!conversationId) return;
      try {
        setLoading(true);
        setError(null);
  
        let allMessages = [];
        let nextToken = null;
  
        do {
          const response = await client.graphql({
            query: messagesByConversationId,
            variables: {
              conversationId,
              sortDirection: 'ASC',
              limit: 100,
              nextToken,
            },
          });
  
          const items = response.data.messagesByConversationId.items;
          allMessages = [...allMessages, ...items];
          nextToken = response.data.messagesByConversationId.nextToken;
        } while (nextToken);
        nextTokenRef.current = nextToken; // set after final page

        if (!isMounted) return;
  
        const uniqueMessages = Array.from(new Map(allMessages.map(msg => [msg.id, msg])).values());
        const sorted = uniqueMessages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        setMessages(sorted);
  
        const lastMsg = sorted[sorted.length - 1];
        if (lastMsg?.timestamp) {
          setLastFetchTime(lastMsg.timestamp);
          console.log("Initial fetch set lastFetchTime to:", lastMsg.timestamp);
        } else {
          console.warn("Latest message has no timestamp, skipping lastFetchTime update.");
        }
  
        setLoading(false);
        setTimeout(() => scrollToBottom(), 50);
  
        if (sorted.some(msg => !msg.read && msg.direction === 'inbound')) {
          try {
            const result = await client.graphql({
              query: mutations.markMessagesAsRead,
              variables: { conversationId }
            });
            
            if (result.data.markMessagesAsRead.success) {
              console.log(`Marked ${result.data.markMessagesAsRead.markedCount} messages as read`);
              // Update messages state to reflect read status
              setMessages(prev => prev.map(msg => 
                msg.direction === 'inbound' && !msg.read 
                  ? { ...msg, read: true, readAt: new Date().toISOString() }
                  : msg
              ));
            }
          } catch (error) {
            console.error('Error marking messages as read:', error);
          }
        }
  
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching messages:', error);
          setLoading(false);
          setError('Failed to load messages');
        }
      }
    };
  
    fetchMessages();
  
    return () => { isMounted = false };
  }, [conversationId]);

  const [lastFetchTime, setLastFetchTime] = useState(new Date().toISOString());
  const lastFetchTimeRef = useRef(lastFetchTime);

  // Keep the ref in sync with state
  useEffect(() => {
    lastFetchTimeRef.current = lastFetchTime;
  }, [lastFetchTime]);


  // Set up subscription with broader criteria
  useEffect(() => {
  if (!conversationId) return;

  console.log("Setting up message subscription for conversationId:", conversationId);

  // Subscribe to ALL message creations and filter client-side
  const subscription = client.graphql({
    query: onCreateMessage
  }).subscribe({
    next: (result) => {
      console.log("Subscription received:", result);
      if (result.data && result.data.onCreateMessage) {
        const newMessage = result.data.onCreateMessage;
        // Only add if it belongs to our conversation
        if (newMessage.conversationId === conversationId) {
          console.log("Adding new message from subscription:", newMessage);
          setMessages(prev => {
            // Avoid duplicates by checking if message already exists
            if (prev.some(msg => msg.id === newMessage.id)) {
              return prev;
            }
            return [...prev, newMessage];
          });
          scrollToBottom();
        }
      }
    },
    error: error => console.error('Subscription error:', error)
  });

  return () => {
    console.log("Cleaning up message subscription");
    subscription.unsubscribe();
  };
}, [conversationId]);


  // Add polling as a backup
  const nextTokenRef = useRef(null);

  useEffect(() => {
    if (!conversationId) return;
  
    const intervalId = setInterval(async () => {
      if (messages.length === 0) {
        console.log("Skipping poll — waiting for initial load.");
        return;
      }
  
      const pollingToken = nextTokenRef.current ?? null;
  
      console.log(`Polling — conversationId: ${conversationId}, token:`, pollingToken);
  
      try {
        const response = await client.graphql({
          query: messagesByConversationId,
          variables: {
            conversationId,
            nextToken: pollingToken, // this is null when caught up
            sortDirection: 'ASC',
            limit: 20
          }
        });
  
        const newMessages = response.data.messagesByConversationId?.items || [];
        const newToken = response.data.messagesByConversationId?.nextToken ?? null;
  
        console.log("Polling received:", newMessages.length, "new messages");
  
        if (newMessages.length > 0) {
          const existingIds = new Set(messages.map(msg => msg.id));
          const filtered = newMessages.filter(msg => !existingIds.has(msg.id));
  
          if (filtered.length > 0) {
            const combined = [...messages, ...filtered].sort(
              (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
            );
            setMessages(combined);
            scrollToBottom();
          } else {
            console.log("Received messages already in state — likely timestamp overlap or no new ones.");
          }
        }
  
        nextTokenRef.current = newToken; // ✅ always update
      } catch (err) {
        console.error("Polling error:", err);
      }
    }, 2000); // adjust as needed
  
    return () => clearInterval(intervalId);
  }, [conversationId, messages]);
  
  // Scroll to bottom when messages change
  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };
  
  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type (images only)
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Only image files (JPEG, PNG, GIF, WebP) are supported');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setSelectedFile(file);
    setError(null);

    // Create preview URL
    const reader = new FileReader();
    reader.onload = () => setFilePreview(reader.result);
    reader.readAsDataURL(file);
  };

  // Remove selected file
  const removeSelectedFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Convert file to base64 data URI
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Send a new message
  const sendMessage = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && !selectedFile) || !conversationId || !submission) return;
    
    try {
      setSending(true);
      setError(null);
      
      // Convert file to base64 data URI if attached
      let mediaData = null;
      if (selectedFile) {
        mediaData = await fileToBase64(selectedFile);
      }
      
      // Send the message through the API
      try {
        const author = 'DigitalPathways';
        await sendConversationMessage(
          conversationId,
          newMessage || (selectedFile ? '' : ''), // Message content
          author,
          mediaData // base64 data URI (e.g. data:image/jpeg;base64,...)
        );
      } catch (smsError) {
        console.error('Error sending SMS via API:', smsError);
      }
      
      setNewMessage('');
      removeSelectedFile();
      setSending(false);
    } catch (error) {
      console.error('Error sending message:', error);
      setSending(false);
      setError('Failed to send message');
    }
  };

  // Helper to parse mediaUrls from message (can be JSON string, array, or object)
  const parseMediaUrls = (mediaUrls) => {
    if (!mediaUrls) return [];

    const s3BaseUrl = process.env.REACT_APP_S3_PUBLIC_BASE_URL || 'https://mhas3bucket0779e-dev.s3.amazonaws.com/';

    const guessContentTypeFromUrl = (url) => {
      if (!url || typeof url !== 'string') return '';
      if (url.startsWith('data:image/')) return 'image/';
      if (url.startsWith('data:video/')) return 'video/';
      if (url.startsWith('data:audio/')) return 'audio/';
      const ext = url.split('?')[0].split('#')[0].split('.').pop()?.toLowerCase();
      if (!ext) return '';
      if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return 'image/';
      if (['mp4', 'mov', 'm4v', 'webm'].includes(ext)) return 'video/';
      if (['mp3', 'wav', 'ogg', 'm4a'].includes(ext)) return 'audio/';
      return '';
    };

    const normalizeMedia = (media) => {
      if (!media) return null;
      if (typeof media === 'string') {
        const url = media.trim();
        return { url, contentType: guessContentTypeFromUrl(url) };
      }
      const rawUrl = media.url || media.mediaUrl || media.link || media.href || '';
      const s3Key = media.s3Key || media.s3_key || '';
      let url = typeof rawUrl === 'string' ? rawUrl.trim() : '';

      if (!url && s3Key) {
        url = `${s3BaseUrl}${s3Key}`;
      }

      const contentType = media.contentType || media.content_type || media.mimeType || guessContentTypeFromUrl(url);
      return url ? { ...media, url, contentType } : null;
    };

    try {
      let parsed = typeof mediaUrls === 'string' ? JSON.parse(mediaUrls) : mediaUrls;
      if (typeof parsed === 'string') {
        parsed = JSON.parse(parsed);
      }
      console.log("Parsed media URLs:", parsed);
      if (Array.isArray(parsed)) {
        return parsed.map(normalizeMedia).filter(Boolean);
      }
      const single = normalizeMedia(parsed);
      return single ? [single] : [];
    } catch {
      return [];
    }
  };
  
  // Group messages by date
  const groupMessagesByDate = () => {
    const groups = {};
    
    messages.forEach(message => {
      const date = new Date(message.timestamp);
      const dateKey = date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
      
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      
      groups[dateKey].push(message);
    });
    
    return groups;
  };
  
  // Format time for display
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  const groupedMessages = groupMessagesByDate();
  
  // Check if we have any data to display
  if (!submissionId || !conversationId) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100">
        <p className="text-gray-500">Please select a conversation to view</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col max-h-[75vh] h-full bg-gray-100">
      {/* Main chat container */}
      <div className="flex-1 overflow-y-auto p-4 min-h-0">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full">
            <RefreshCw size={24} className="text-blue-500 animate-spin mb-2" />
            <p className="text-gray-500">Loading messages...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full text-red-500">
            <p className="mb-2">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200"
            >
              Try Again
            </button>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <p>No messages yet</p>
            <p className="text-sm mt-1">Send a message to start the conversation</p>
          </div>
        ) : (
          Object.keys(groupedMessages).map(date => (
            <div key={date}>
              <div className="flex justify-center my-4">
                <div className="bg-gray-200 rounded-full px-4 py-1 text-xs text-gray-600">
                  {date}
                </div>
              </div>
              
                {groupedMessages[date].map(message => (
                <div 
                  key={message.id} 
                  className={`flex mb-3 ${message.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                >
                  <div>
                    {message.direction === 'inbound' && (
                      <div className="text-xs text-gray-500 mb-1 ml-2">
                        <strong>
                        <span>({message.phoneNumber})</span>
                        </strong>
                        ({message.name || 'Unknown'})
                        {' '}                        
                      </div>
                    )}
                    <div 
                      className={`px-4 py-2 rounded-lg max-w-xs break-words ${
                        message.direction === 'outbound' 
                          ? 'bg-blue-500 text-white rounded-br-none' 
                          : 'bg-white text-gray-800 rounded-bl-none shadow'
                      }`}
                    >
                      {/* Render media/images if present */}
                      {parseMediaUrls(message.mediaUrls).map((media, idx) => {
                        const ct = media.contentType || media.content_type || '';
                        console.log("Content type:", ct);
                        const isImage = ct.startsWith('image/');
                        const isVideo = ct.startsWith('video/');
                        const isAudio = ct.startsWith('audio/');

                        if (isImage) {
                          return (
                            <a key={idx} href={media.url} target="_blank" rel="noopener noreferrer">
                              <img 
                                src={media.url} 
                                alt="attachment" 
                                className="rounded-md mb-2 max-w-full max-h-48 object-contain cursor-pointer"
                              onError={(e) => { e.target.style.opacity = '0'; }}
                              />
                            </a>
                          );
                        }

                        if (isVideo) {
                          return (
                            <video 
                              key={idx}
                              src={media.url}
                              controls
                              className="rounded-md mb-2 max-w-full max-h-56"
                            />
                          );
                        }

                        if (isAudio) {
                          return (
                            <audio 
                              key={idx}
                              src={media.url}
                              controls
                              className="w-full mb-2"
                            />
                          );
                        }

                        return (
                          <a 
                            key={idx} 
                            href={media.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="block text-sm underline mb-2"
                          >
                            📎 Attachment
                          </a>
                        );
                      })}
                      {message.content && <p>{message.content}</p>}
                      <div className={`flex justify-end items-center mt-1 text-xs ${
                        message.direction === 'outbound' ? 'text-blue-100' : 'text-gray-500'
                      }`}>
                        {formatTime(message.timestamp)}
                        {message.direction === 'outbound' && (
                          <span className="ml-1.5">✓</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Message input */}
      <div className="bg-white border-t p-3">
        {error && (
          <div className="bg-red-100 text-red-700 text-sm p-2 mb-2 rounded">
            {error}
          </div>
        )}
        {/* File preview */}
        {filePreview && (
          <div className="relative inline-block mb-2">
            <img 
              src={filePreview} 
              alt="preview" 
              className="h-20 w-20 object-cover rounded-lg border border-gray-200"
            />
            <button
              type="button"
              onClick={removeSelectedFile}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"
            >
              <X size={14} />
            </button>
            <div className="text-xs text-gray-500 mt-1 truncate max-w-[80px]">
              {selectedFile?.name}
            </div>
          </div>
        )}
        <form onSubmit={sendMessage} className="flex items-center">
          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/jpeg,image/png,image/gif,image/webp"
            className="hidden"
          />
          <button 
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={sending || loading}
            className={`p-2 rounded-full ${
              selectedFile 
                ? 'text-blue-500 hover:text-blue-700' 
                : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            {selectedFile ? <ImageIcon size={20} /> : <Paperclip size={20} />}
          </button>
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={selectedFile ? "Add a caption..." : "Type a message..."}
            disabled={sending || loading}
            className="flex-1 border rounded-full py-2 px-4 mx-2 focus:outline-none focus:ring-2 focus:ring-blue-300"
          />
          <button
            type="submit"
            disabled={(!newMessage.trim() && !selectedFile) || sending || loading}
            className={`rounded-full p-2 flex items-center justify-center ${
              (!newMessage.trim() && !selectedFile) || sending || loading 
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                : 'bg-blue-500 text-white hover:bg-blue-600'
            }`}
          >
            {sending ? (
              <RefreshCw size={20} className="animate-spin" />
            ) : (
              <Send size={20} />
            )}
          </button>
        </form>
        {/* Render participants */}
        <Participants 
          conversationId={conversationId} 
          participants={participants}
          setParticipants={setParticipants}
        />
      </div>
    </div>
  );
};

export default SMSConversation;
